/**
 * RequestLayoutShell — the request-screen chrome (Header + request frame + sidebar + screen pane).
 *
 * Renders ONE tree for both the desktop (`>=1200`) and mobile (`<1200`) layouts so that React's
 * child-by-index reconciliation never tears down the screen subtree when the width crosses the
 * breakpoint. Previously `App.tsx` rendered two structurally different trees; index 0 was
 * `Sidebar` vs `View` and index 1 was `View` vs `SidebarOverlay`, so every crossing unmounted and
 * remounted the whole request screen and destroyed component-local state (#1437 subsection expand,
 * #1439 Request Detail edit mode, #1440 spurious draft-restored toast).
 *
 * Slot contract:
 *   slot 0 — sidebar rail. `Sidebar` vs `SidebarPreview`; different component types, so this slot
 *            genuinely remounts on every crossing. That is intended and harmless: their
 *            collapse/visibility state lives in `App`, above this component.
 *   slot 1 — screen holder `View`. Same type at the same index in both layouts, so the screen
 *            subtree (and all of its local state) is preserved.
 *   The `SidebarOverlay` sibling is written as `{cond ? <SidebarOverlay/> : null}` — a literal JSX
 *   child that occupies exactly one implicit-key slot whether or not it renders, so it cannot shift
 *   the row container's index.
 *
 * Presentational only: every value arrives as a prop (notably `effectiveRequestStage`, which is
 * derived in `AppContentWithQuery` from the `useRequestStage` hook and must stay there).
 */
import React from 'react';
import { Platform, View } from 'react-native';

import { Header } from './Header';
import { KeyboardDismissToolbar } from './shared/KeyboardDismissToolbar';
import { RichTextKeyboardDismissBar } from './shared/RichTextKeyboardDismissBar';
import { Sidebar } from './Sidebar';
import { SidebarOverlay } from './SidebarOverlay';
import { SidebarPreview } from './SidebarPreview';
import {
  RequestFrameProvider,
  RequestFrameToolbarSlot,
} from '../contexts/RequestFrameContext';
import { RequestSaveTransitionProvider } from '../contexts/RequestSaveTransitionContext';
import { RequestScopeProvider } from '../contexts/RequestScopeContext';
import { RichTextKeyboardChromeProvider } from '../contexts/RichTextKeyboardChromeContext';

export interface RequestLayoutShellProps {
  /** `screenWidth < 1200` — selects the sidebar rail variant only. */
  isMobileLayout: boolean;
  renderCurrentScreen: () => React.ReactNode;
  navigate: (path: string) => void;
  currentRoute: string;
  recordingState: string;
  currentRequestId?: string | null;
  currentFormStep?: string;
  setCurrentFormStep?: (stepId: string) => void;
  effectiveRequestStage?: string;
  isITAdministrator?: boolean;
  sidebarCollapsed?: boolean;
  toggleSidebarCollapse?: () => void;
  leftSidebarVisible: boolean;
  setLeftSidebarVisible: (visible: boolean) => void;
}

export const RequestLayoutShell: React.FC<RequestLayoutShellProps> = ({
  isMobileLayout,
  renderCurrentScreen,
  navigate,
  currentRoute,
  recordingState,
  currentRequestId,
  currentFormStep,
  setCurrentFormStep: setCurrentFormStepWithPersistence,
  effectiveRequestStage,
  isITAdministrator,
  sidebarCollapsed,
  toggleSidebarCollapse,
  leftSidebarVisible,
  setLeftSidebarVisible,
}) => {
  return (
    <View
      testID="request-layout-shell"
      style={{
        flex: 1,
        ...Platform.select({
          web: {
            // 100dvh tracks the visible phone viewport. 100vh clips the
            // request footer under the browser chrome (#1632).
            height: '100dvh' as any,
            overflow: 'hidden' as any,
          },
          default: {},
        }),
      }}
    >
      <Header
        navigate={navigate}
        currentPath={currentRoute.split('?')[0]}
        disableMenu={recordingState !== 'idle'}
      />
      <RequestFrameProvider>
        <RequestScopeProvider>
          <RichTextKeyboardChromeProvider>
            <RequestSaveTransitionProvider>
              <View
                style={{
                  flex: 1,
                  overflow: 'hidden' as any,
                }}
              >
                <RequestFrameToolbarSlot />
                <View
                  style={{
                    flex: 1,
                    position: 'relative',
                    overflow: 'hidden' as any,
                  }}
                >
                  <View
                    style={{
                      flex: 1,
                      flexDirection: 'row',
                      overflow: 'hidden' as any,
                    }}
                  >
                    {isMobileLayout ? (
                      <SidebarPreview
                        key={`mobile-preview-${currentRequestId || 'default'}-${
                          effectiveRequestStage || 'none'
                        }`}
                        activeItem={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                            ? currentFormStep
                            : 'opportunity'
                        }
                        navigate={navigate}
                        onStepChange={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                            ? setCurrentFormStepWithPersistence
                            : undefined
                        }
                        showSummaryStep={true}
                        onExpand={() => setLeftSidebarVisible(true)}
                        usePermissionFiltering={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                        }
                        requestStage={effectiveRequestStage}
                        requestId={
                          currentRoute.split('?')[0] === '/create-request'
                            ? 'new-request'
                            : currentRequestId
                        }
                        isITAdministrator={isITAdministrator}
                        embeddedInFrame={true}
                      />
                    ) : (
                      <Sidebar
                        key={`desktop-sidebar-${currentRequestId || 'default'}`}
                        activeItem={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                            ? currentFormStep
                            : 'opportunity'
                        }
                        isCollapsed={sidebarCollapsed}
                        onToggleCollapse={toggleSidebarCollapse}
                        navigate={navigate}
                        onStepChange={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                            ? setCurrentFormStepWithPersistence
                            : undefined
                        }
                        showSummaryStep={true}
                        usePermissionFiltering={
                          currentRoute.split('?')[0] === '/create-request' ||
                          currentRoute.split('?')[0] === '/request-detail'
                        }
                        requestStage={effectiveRequestStage}
                        requestId={
                          currentRoute.split('?')[0] === '/create-request'
                            ? 'new-request'
                            : currentRequestId
                        }
                        isITAdministrator={isITAdministrator}
                        embeddedInFrame={true}
                      />
                    )}
                    <View
                      style={{
                        flex: 1,
                        overflow: 'hidden' as any,
                      }}
                    >
                      {renderCurrentScreen()}
                    </View>
                  </View>
                  {isMobileLayout ? (
                    <SidebarOverlay
                      key={`mobile-overlay-${currentRequestId || 'default'}-${
                        effectiveRequestStage || 'none'
                      }`}
                      isVisible={leftSidebarVisible}
                      onClose={() => setLeftSidebarVisible(false)}
                      activeItem={
                        currentRoute.split('?')[0] === '/create-request' ||
                        currentRoute.split('?')[0] === '/request-detail'
                          ? currentFormStep
                          : 'opportunity'
                      }
                      navigate={navigate}
                      onStepChange={
                        currentRoute.split('?')[0] === '/create-request' ||
                        currentRoute.split('?')[0] === '/request-detail'
                          ? setCurrentFormStepWithPersistence
                          : undefined
                      }
                      showSummaryStep={true}
                      usePermissionFiltering={
                        currentRoute.split('?')[0] === '/create-request' ||
                        currentRoute.split('?')[0] === '/request-detail'
                      }
                      requestStage={effectiveRequestStage}
                      requestId={
                        currentRoute.split('?')[0] === '/create-request'
                          ? 'new-request'
                          : currentRequestId
                      }
                      isITAdministrator={isITAdministrator}
                      embeddedInFrame={true}
                    />
                  ) : null}
                  <KeyboardDismissToolbar />
                  <RichTextKeyboardDismissBar />
                </View>
              </View>
            </RequestSaveTransitionProvider>
          </RichTextKeyboardChromeProvider>
        </RequestScopeProvider>
      </RequestFrameProvider>
    </View>
  );
};

export default RequestLayoutShell;
